import { useState, useEffect } from 'react';
import { alterationAnalyticsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Minus, Activity, Filter, AlertTriangle,
  Shirt, Info, RefreshCw, BarChart3,
} from 'lucide-react';

const GARMENT_LABELS = {
  jacket: 'Jacket',
  trousers: 'Trousers',
  shirt: 'Shirt',
  sherwani: 'Sherwani',
  bandhgala: 'Bandhgala',
  nehru_vest: 'Nehru Vest',
  kurta: 'Kurta',
  pyjama: 'Pyjama',
  waistcoat: 'Waistcoat',
  unknown: 'Unknown',
};

const AREA_LABELS = {
  collar: 'Collar/Lapels',
  shoulders: 'Shoulders',
  chest: 'Chest',
  waist: 'Waist',
  hem: 'Hem',
  sleeve: 'Sleeves',
  cuff: 'Cuffs',
  back: 'Back',
  seat: 'Seat/Hips',
  thigh: 'Thigh',
  knee: 'Knee',
  calf: 'Calf',
  general: 'General',
};

const TYPE_LABELS = {
  shorten: 'Shorten',
  lengthen: 'Lengthen',
  take_in: 'Take In',
  let_out: 'Let Out',
  reshape: 'Reshape',
  reposition: 'Reposition',
  narrow: 'Narrow',
  widen: 'Widen',
  adjustment: 'Adjustment',
};

export default function AlterationPatternsPage() {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState('6');
  const [filterGarment, setFilterGarment] = useState('all');

  const fetchPatterns = async () => {
    setLoading(true);
    try {
      const res = await alterationAnalyticsAPI.patterns(parseInt(months));
      setPatterns(res.data || []);
    } catch (err) {
      toast.error('Failed to load alteration patterns');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatterns();
  }, [months]);

  const filteredPatterns = filterGarment === 'all'
    ? patterns
    : patterns.filter(p => p.garment_type === filterGarment);

  // Identify high-frequency patterns (>15%)
  const highFreqPatterns = filteredPatterns.filter(p => p.frequency_pct > 15);
  const upwardTrends = filteredPatterns.filter(p => p.trend === 'up');

  // Group patterns by garment for summary
  const garmentSummary = patterns.reduce((acc, p) => {
    if (!acc[p.garment_type]) {
      acc[p.garment_type] = { count: 0, total_freq: 0 };
    }
    acc[p.garment_type].count += p.count;
    acc[p.garment_type].total_freq += p.frequency_pct;
    return acc;
  }, {});

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-red-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-green-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getTrendBadge = (trend) => {
    const styles = {
      up: { bg: 'rgba(239,68,68,0.1)', color: '#DC2626', label: 'Increasing' },
      down: { bg: 'rgba(34,197,94,0.1)', color: '#16A34A', label: 'Decreasing' },
      stable: { bg: 'rgba(107,114,128,0.1)', color: '#6B7280', label: 'Stable' },
    };
    const s = styles[trend] || styles.stable;
    return (
      <Badge className="text-[10px] font-medium" style={{ backgroundColor: s.bg, color: s.color }}>
        {s.label}
      </Badge>
    );
  };

  const getFrequencyColor = (pct) => {
    if (pct >= 20) return { bg: 'rgba(239,68,68,0.15)', text: '#DC2626' };
    if (pct >= 10) return { bg: 'rgba(245,158,11,0.15)', text: '#D97706' };
    if (pct >= 5) return { bg: 'rgba(59,130,246,0.1)', text: '#2563EB' };
    return { bg: 'rgba(107,114,128,0.08)', text: '#6B7280' };
  };

  return (
    <div className="space-y-6 p-6" data-testid="alteration-patterns-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--brand-navy)' }}>
            Alteration Patterns
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
            Identify frequent alteration areas to improve garment patterns
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchPatterns}
          disabled={loading}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-xl border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(27,42,74,0.08)' }}>
                <BarChart3 className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Total Patterns</p>
                <p className="text-xl font-bold" style={{ color: 'var(--brand-navy)' }}>
                  {loading ? '-' : patterns.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(239,68,68,0.1)' }}>
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>High Frequency (&gt;15%)</p>
                <p className="text-xl font-bold text-red-600">
                  {loading ? '-' : highFreqPatterns.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(245,158,11,0.1)' }}>
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Upward Trends</p>
                <p className="text-xl font-bold text-amber-600">
                  {loading ? '-' : upwardTrends.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}>
                <Activity className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Garment Types</p>
                <p className="text-xl font-bold text-green-600">
                  {loading ? '-' : Object.keys(garmentSummary).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="rounded-xl border-border/80 bg-white shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>Filters:</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Time Range:</span>
              <Select value={months} onValueChange={setMonths}>
                <SelectTrigger className="w-[140px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Last 3 Months</SelectItem>
                  <SelectItem value="6">Last 6 Months</SelectItem>
                  <SelectItem value="12">Last 12 Months</SelectItem>
                  <SelectItem value="24">Last 24 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Garment:</span>
              <Select value={filterGarment} onValueChange={setFilterGarment}>
                <SelectTrigger className="w-[140px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Garments</SelectItem>
                  {Object.entries(GARMENT_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Patterns Table */}
      <Card className="rounded-xl border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Shirt className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
            Alteration Frequency by Area
          </CardTitle>
          <CardDescription className="text-xs">
            Patterns sorted by frequency percentage of total orders
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredPatterns.length === 0 ? (
            <div className="text-center py-8">
              <Info className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--ink-muted)' }} />
              <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
                No alteration patterns found for the selected period
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Garment</TableHead>
                  <TableHead className="text-xs">Area</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs text-right">Count</TableHead>
                  <TableHead className="text-xs text-right">Frequency</TableHead>
                  <TableHead className="text-xs text-center">Trend</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPatterns.map((pattern, idx) => {
                  const freqColor = getFrequencyColor(pattern.frequency_pct);
                  return (
                    <TableRow key={idx} data-testid={`pattern-row-${idx}`}>
                      <TableCell className="font-medium text-sm">
                        {GARMENT_LABELS[pattern.garment_type] || pattern.garment_type}
                      </TableCell>
                      <TableCell className="text-sm">
                        {AREA_LABELS[pattern.area] || pattern.area}
                      </TableCell>
                      <TableCell className="text-sm">
                        <Badge variant="outline" className="text-xs">
                          {TYPE_LABELS[pattern.alteration_type] || pattern.alteration_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {pattern.count}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge
                          className="text-xs font-bold px-2"
                          style={{ backgroundColor: freqColor.bg, color: freqColor.text }}
                        >
                          {pattern.frequency_pct}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {getTrendIcon(pattern.trend)}
                          {getTrendBadge(pattern.trend)}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Insights Card */}
      {highFreqPatterns.length > 0 && (
        <Card className="rounded-xl border-2" style={{ borderColor: 'rgba(239,68,68,0.3)', backgroundColor: 'rgba(239,68,68,0.03)' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-4 h-4" />
              Pattern Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {highFreqPatterns.slice(0, 5).map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span style={{ color: 'var(--navy-ink)' }}>
                    <strong>{GARMENT_LABELS[p.garment_type]}</strong> - {AREA_LABELS[p.area] || p.area}:{' '}
                    {TYPE_LABELS[p.alteration_type]} occurs in <strong>{p.frequency_pct}%</strong> of orders
                    {p.trend === 'up' && <span className="text-red-600 font-medium"> (trending up)</span>}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs mt-4" style={{ color: 'var(--ink-muted)' }}>
              Consider reviewing garment patterns for these areas to reduce alteration frequency
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
